import { useTranslations } from "next-intl";
import { ProductData } from "@/lib/sanity/responseTypes";
import { formatMoney } from "@/lib/money-utils";
import { useEffect, useState } from "react";
import Link from "@/components/NavigationLink";
import ProductAttribute from "./product-attributes";
import ArrowDown from "@/components/icons/arrow-down.svg";
import BuildingStorefront from '@/components/icons/building-storefront.svg';
import Globe from '@/components/icons/globe-alt.svg';
import Heart from "@/components/icons/heart.svg";
import HeartSolid from "@/components/icons/heart-solid.svg";
import ShoppingBag from '@/components/icons/shopping-bag.svg';
import ShoppingCart from '@/components/icons/shopping-cart.svg';
import Truck from '@/components/icons/truck.svg';
import { useRouter } from "@/i18n/navigation";

interface ProductSelectorProps {
  product: ProductData;
  inWishlist?: boolean;
  locale: string;
  t: ReturnType<typeof useTranslations>;
  tUnits: ReturnType<typeof useTranslations>;
  addToCart: (qty: number, unitPrice: number) => void;
  toggleWishlist: (() => void) | (() => Promise<void>);
  lockUIAddToCart?: boolean;
  lockUIToggleWishlist?: boolean;
}

function ProductSelector(props: ProductSelectorProps) {
  const { product, inWishlist, locale, t, tUnits } = props;
  const router = useRouter();

  const [currentQuantity, setCurrentQuantity] = useState<number>(1);
  const [relevantPrice, setRelevantPrice] = useState<number>(product.price);
  const [relevantVolumeDiscount, setRelevantVolumeDiscount] = useState<{ quantity: number, unitPrice: number, currency: string } | null>(null)
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isTogglingWishlist, setIsTogglingWishlist] = useState(false);

  useEffect(() => {
    let newRelevantPrice: number = product.price;
    let newRelevantDiscount: { quantity: number, unitPrice: number, currency: string } | null = null;

    if (product.volumeDiscount) {
      const fullDiscountsList: { quantity: number, unitPrice: number, currency: string }[] = [
        { quantity: 1, unitPrice: product.price, currency: product.currency },
        ...product.volumeDiscount
      ];

      for (let d of fullDiscountsList) {
        if (currentQuantity >= d.quantity) {
          newRelevantPrice = d.unitPrice;
          newRelevantDiscount = d;
        }
      }
    }

    if (newRelevantPrice != relevantPrice) {
      setRelevantPrice(newRelevantPrice);
      setRelevantVolumeDiscount(newRelevantDiscount);
    }
  }, [currentQuantity]);

  function minusQty() {
    if (currentQuantity > 1) {
      setCurrentQuantity(currentQuantity - 1);
    }
  }

  function plusQty() {
    setCurrentQuantity(currentQuantity + 1);
  }

  return (
    <>
      {/* Desktop version */}
      < div className="hidden md:flex md:flex-1 flex-col items-start gap-2.5" >
        <div className="self-stretch relative text-[32px] leading-[120%] flex items-center shrink-0 font-bold">
          {product.name}
        </div>
        <div className="self-stretch relative text-base leading-5 h-4 flex shrink-0">
          {product.intro}
        </div>
        <Link href="#product-details" className="p-0">
          <div className="btn btn-ghost text-black h-6">
            {t('details_title')}
            <ArrowDown className="h-[11px] w-[11px] stroke-black stroke-1" />
          </div>
        </Link>
        <div className="w-full relative overflow-hidden flex md:flex-row flex-col md:align-start items-center md:justify-center px-[30px] pb-[30px] box-border gap-5 text-center text-base font-[Outfit] text-primary">
          {product.attributes && product.attributes.map((a, index) => {
            return (
              <div key={`attribute-${index}`} className="w-[180px] overflow-hidden shrink-0 flex flex-col items-center justify-center p-2.5 box-border gap-2.5">
                <div className="w-15 h-15 relative rounded-[15px] bg-white overflow-hidden shrink-0 text-center place-items-center">
                  <div className="w-full h-15 bg-secondary absolute top-7.5"></div>
                  <ProductAttribute className="absolute top-2.5 left-2.5 fill-primary stroke-primary h-10 w-10 stroke-3" slug={a.slug} />
                </div>
                <div className="w-[100%] font-bold leading-[120%] wrap-break-word hyphens-auto overflow-hidden">{a.label[locale]}</div>
              </div>
            )
          })}
        </div>
        <div className="w-full md:w-[720px] relative shrink-0 grid grid-cols-1 grid-rows-1 md:grid-cols-2 md:grid-rows-1 justify-start content-start gap-2.5 font-[Outfit]">
          <div className="md:w-[340px] w-full flex flex-col gap-2.5">
            <div className="relative text-[32px] leading-[32px] inline-block overflow-hidden overflow-ellipsis whitespace-nowrap min-h-10 shrink-0">
              {formatMoney(relevantPrice)}*
            </div>
            <div className="self-stretch relative text-xs leading-[150%] font-light text-[rgba(0,0,0,0.6)] shrink-0">
              {t('price-excl-tax')}
              <br />
              {t('price-special-on-login')}
            </div>
            {product.volumeDiscount && (<div className="self-stretch overflow-hidden flex items-start flex-wrap content-start py-2.5 gap-2.5 shrink-0 px-0.5">
              {[{ quantity: 1, unitPrice: product.price, currency: product.currency }, ...product.volumeDiscount].map((d, i) => {
                let isCurrentDiscount = relevantVolumeDiscount && d.quantity === relevantVolumeDiscount.quantity;
                return (
                  <div key={`volume-discount-${i}`} className={
                    `h-20 rounded-lg ${isCurrentDiscount ? "outline-secondary" : "border-[rgba(0,0,0,0.6)]"} box-border outline-1 overflow-hidden flex flex-col items-center py-2 px-[9px] gap-2.5 shrink-0 cursor-pointer hover:outline-secondary transition`
                  } onClick={() => setCurrentQuantity(d.quantity)}>
                    <div className="w-[84px] relative font-semibold leading-[100%]">{t('price-from')} {d.quantity} {d.quantity === 1 ? t('price-unit-singular') : t('price-unit-plural')}</div>
                    <div className="w-[84px] relative text-secondary leading-[100%]">{formatMoney(d.unitPrice)}</div>
                    {d.quantity !== 1 && (<div className="w-[84px] relative font-semibold leading-[100%]">-{Math.round(100 * (1 - d.unitPrice / product.price))}%</div>)}
                  </div>
                )
              })}
            </div>
            )}
            <div className="self-stretch relative text-xs leading-[150%] font-light text-[rgba(0,0,0,0.6)] shrink-0">
              {t('packaging')}: {product.packagingQty} {tUnits(product.packagingType || "")}
            </div>
            <div className="h-8 relative text-base font-medium leading-[180%] flex shrink-0">
              {t('filter_units')}
            </div>
            <div className="join md:w-[340px] w-full">
              <button className="btn join-item rounded-l-full" onClick={minusQty}>
                -
              </button>
              <input className="input join-item w-full" type="number" value={currentQuantity} onChange={(e) => !isNaN(parseInt(e.target.value)) && setCurrentQuantity(parseInt(e.target.value))} />
              <button className="btn join-item rounded-r-full" onClick={plusQty}>
                +
              </button>
            </div>
            <div>
              <button className="btn btn-secondary text-white rounded-full md:w-[340px] w-full" onClick={async () => {
                if (isAddingToCart || isTogglingWishlist) return;
                setIsAddingToCart(true);
                try { await Promise.resolve(props.addToCart(currentQuantity, relevantPrice)); }
                finally { setIsAddingToCart(false); }
              }} disabled={isAddingToCart || isTogglingWishlist}>
                {isAddingToCart ? <span className="loading loading-spinner loading-md">{" "}</span> : <ShoppingCart className="h-3.5 w-3.5 stroke-white" />}
                {t('field-submit')}
              </button>
            </div>
            <div>
              <button className="btn rounded-full md:w-[340px] w-full" onClick={async () => {
                if (isTogglingWishlist || isAddingToCart) return;
                setIsTogglingWishlist(true);
                try { await Promise.resolve(props.toggleWishlist()); }
                finally { setIsTogglingWishlist(false); }
              }} disabled={isTogglingWishlist || isAddingToCart}>
                {isTogglingWishlist ? <span className="loading loading-spinner loading-md">{" "}</span> : (props.inWishlist ? <HeartSolid className="h-3.5 w-3.5 fill-secondary" /> : <Heart className="h-3.5 w-3.5 stroke-black" />)}
                {t('field-wishlist')}
              </button>
            </div>
          </div>
          <div className="md:w-[370px] w-full flex flex-col gap-2.5">
            {!product.disableFamilyGrouping && product.colorVariants && product.colorVariants.length > 1 ? (
              <>
                <div className="relative self-stretch text-base font-medium">
                  {t('filter_color')}
                </div>
                {product.colorVariants && product.colorVariants.length > 10 ? (
                  <select className="select w-full" value={product.colorVariants.find(c => c.current)?.product.productPageUrl} onChange={(e) => router.push(e.target.value)}>
                    {product.colorVariants.map((c, i) => {
                      return (
                        <option key={`color-${c.dimensionLabel.trim().replace(' ', '')}-desktop`} value={c.product.productPageUrl}>{c.dimensionLabel}</option>
                      )
                    })}
                  </select>
                ) : (
                  <div className="flex flex-row flex-wrap px-0 py-2.5 gap-2.5 items-start content-start box-border w-full">
                    {product.colorVariants.map((c, i) => {
                      return (
                        <Link key={`color-${i}`} href={c.product.productPageUrl} className="p-0 box-border">
                          <button className={c.current ? "btn btn-secondary text-white" : "btn"}>{c.dimensionLabel}</button>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </>
            ) : null}
            {!product.disableFamilyGrouping && product.sizeVariants && product.sizeVariants.length > 1 ? (
              <>
                <div className="relative self-stretch text-base font-medium">
                  {t('filter_quantity')}
                </div>
                {product.sizeVariants && product.sizeVariants.length > 10 ? (
                  <select className="select w-full" value={product.sizeVariants.find(s => s.current)?.product.productPageUrl} onChange={(e) => router.push(e.target.value)}>
                    {product.sizeVariants.map((s, i) => {
                      return (
                        <option key={`size-${s.dimensionLabel.trim().replace(' ', '')}-desktop`} value={s.product.productPageUrl}>{s.dimensionLabel}</option>
                      )
                    })}
                  </select>
                ) : (
                  <div className="flex flex-row flex-wrap px-0 py-2.5 gap-2.5 items-start content-start box-border w-full">
                    {product.sizeVariants.map((s, i) => {
                      return (
                        <Link key={`size-${i}`} href={s.product.productPageUrl} className="p-0 box-border">
                          <button className={s.current ? "btn btn-secondary text-white" : "btn"}>{s.dimensionLabel}</button>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </>
            ) : null}
            <div className="w-full py-[30px] flex flex-col items-start box-border gap-2.5 text-sm">
              <div className="h-8 flex flex-row items-center gap-2.5">
                <Truck height={24} width={24} />
                <div className="flex-1">
                  {t('advantage_delivery_time')}
                </div>
              </div>
              <div className="h-8 flex flex-row items-center gap-2.5">
                <ShoppingBag height={24} width={24} />
                <div className="flex-1">
                  {t('advantage_delivery_free')}
                </div>
              </div>
              <div className="h-8 flex flex-row items-center gap-2.5">
                <BuildingStorefront height={24} width={24} />
                <div className="flex-1">
                  {t('advantage_showroom')}
                </div>
              </div>
              <div className="h-8 flex flex-row items-center gap-2.5">
                <Globe height={24} width={24} />
                <div className="flex-1">
                  {t('advantage_local_resellers')}
                  {" "}
                  <Link className="p-0 transition-colors text-gray-500 hover:text-gray-300" href={"/standorte"}>{t('advantage_local_resellers_link')}</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
      {/* Mobile version */}
      < div className="md:hidden overflow-hidden flex flex-col items-start gap-2.5" >
        <div className="self-stretch relative text-[32px] leading-[120%] flex items-center shrink-0 font-bold">
          {product.name}
        </div>
        <div className="self-stretch relative text-base leading-5 flex shrink-0">
          {product.intro}
        </div>
        <Link href="#product-details" className="p-0">
          <div className="btn btn-ghost text-black">
            {t('details_title')}
            <ArrowDown className="h-[11px] w-[11px] stroke-black stroke-1" />
          </div>
        </Link>
        <div className="w-full relative overflow-hidden flex md:flex-row flex-col md:align-start items-center md:justify-center px-[30px] pb-[30px] box-border gap-5 text-center text-base font-[Outfit] text-primary">
          {product.attributes && product.attributes.map((a, index) => {
            return (
              <div key={`attribute-${index}`} className="w-[180px] overflow-hidden shrink-0 flex flex-col items-center justify-center p-2.5 box-border gap-2.5">
                <div className="w-15 h-15 relative rounded-[15px] bg-white overflow-hidden shrink-0 text-center place-items-center">
                  <div className="w-full h-15 bg-secondary absolute top-7.5"></div>
                  <ProductAttribute className="absolute top-2.5 left-2.5 fill-primary stroke-primary h-10 w-10 stroke-3" slug={a.slug} />
                </div>
                <div className="font-bold leading-[120%]">{a.label[locale]}</div>
              </div>
            )
          })}
        </div>
        <div className="w-full relative overflow-hidden shrink-0 grid grid-cols-1 grid-rows-1 md:grid-cols-2 md:grid-rows-1 justify-start content-start gap-2.5 font-[Outfit]">
          <div className="md:w-[340px] w-full flex flex-col gap-2.5">
            <div className="relative text-[32px] leading-[32px] inline-block overflow-hidden overflow-ellipsis whitespace-nowrap min-h-10 shrink-0">
              {formatMoney(relevantPrice)}*
            </div>
            <div className="self-stretch relative text-xs leading-[150%] font-light text-[rgba(0,0,0,0.6)] shrink-0">
              {t('price-excl-tax')}
              <br />
              {t('price-special-on-login')}
            </div>
            {product.volumeDiscount && (<div className="self-stretch overflow-hidden flex items-start flex-wrap content-start py-2.5 gap-2.5 shrink-0 px-0.5">
              {[{ quantity: 1, unitPrice: product.price, currency: product.currency }, ...product.volumeDiscount].map((d, i) => {
                let isCurrentDiscount = relevantVolumeDiscount && d.quantity === relevantVolumeDiscount.quantity;
                return (
                  <div key={`volume-discount-${i}`} className={
                    `h-20 rounded-lg ${isCurrentDiscount ? "outline-secondary" : "border-[rgba(0,0,0,0.6)]"} box-border outline-1 overflow-hidden flex flex-col items-center py-2 px-[9px] gap-2.5 shrink-0 cursor-pointer hover:outline-secondary transition`
                  } onClick={() => setCurrentQuantity(d.quantity)}>
                    <div className="w-[84px] relative font-semibold leading-[100%]">{t('price-from')} {d.quantity} {d.quantity === 1 ? t('price-unit-singular') : t('price-unit-plural')}</div>
                    <div className="w-[84px] relative text-secondary leading-[100%]">{formatMoney(d.unitPrice)}</div>
                    {d.quantity !== 1 && (<div className="w-[84px] relative font-semibold leading-[100%]">-{Math.round(100 * (1 - d.unitPrice / product.price))}%</div>)}
                  </div>
                )
              })}
            </div>
            )}
            <div className="self-stretch relative text-xs leading-[150%] font-light text-[rgba(0,0,0,0.6)] shrink-0">
              {t('packaging')}: {product.packagingQty} {tUnits(product.packagingType || "")}
            </div>
            {!product.disableFamilyGrouping && product.colorVariants && product.colorVariants.length > 1 ? (
              <>
                <div className="relative self-stretch text-base font-medium">
                  {t('filter_color')}
                </div>
                {product.colorVariants && product.colorVariants.length > 10 ? (
                  <select className="select w-full" value={product.colorVariants.find(c => c.current)?.product.productPageUrl} onChange={(e) => router.push(e.target.value)}>
                    {product.colorVariants.map((c, i) => {
                      return (
                        <option key={`color-${c.dimensionLabel.trim().replace(' ', '')}-mobile`} value={c.product.productPageUrl}>{c.dimensionLabel}</option>
                      )
                    })}
                  </select>
                ) : (
                  <div className="flex flex-row flex-wrap px-0 py-2.5 gap-2.5 items-start content-start box-border w-full">
                    {product.colorVariants.map((c, i) => {
                      return (
                        <Link key={`color-${i}`} href={c.product.productPageUrl} className="p-0 box-border">
                          <button className={c.current ? "btn btn-secondary text-white" : "btn"}>{c.dimensionLabel}</button>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </>
            ) : null}
            {!product.disableFamilyGrouping && product.sizeVariants && product.sizeVariants.length > 1 ? (
              <>
                <div className="relative self-stretch text-base font-medium">
                  {t('filter_quantity')}
                </div>
                {product.sizeVariants && product.sizeVariants.length > 10 ? (
                  <select className="select w-full" value={product.sizeVariants.find(s => s.current)?.product.productPageUrl} onChange={(e) => router.push(e.target.value)}>
                    {product.sizeVariants.map((s, i) => {
                      return (
                        <option key={`size-${s.dimensionLabel.trim().replace(' ', '')}-mobile`} value={s.product.productPageUrl}>{s.dimensionLabel}</option>
                      )
                    })}
                  </select>
                ) : (
                  <div className="flex flex-row flex-wrap px-0 py-2.5 gap-2.5 items-start content-start box-border w-full">
                    {product.sizeVariants.map((s, i) => {
                      return (
                        <Link key={`size-${i}`} href={s.product.productPageUrl} className="p-0 box-border">
                          <button className={s.current ? "btn btn-secondary text-white" : "btn"}>{s.dimensionLabel}</button>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </>
            ) : null}
            <div className="h-8 relative text-base font-medium leading-[180%] flex shrink-0">
              {t('filter_units')}
            </div>
            <div className="join md:w-[340px] w-full">
              <button className="btn join-item rounded-l-full" onClick={minusQty}>
                -
              </button>
              <input className="input join-item w-full" type="number" value={currentQuantity} onChange={(e) => !isNaN(parseInt(e.target.value)) && setCurrentQuantity(parseInt(e.target.value))} />
              <button className="btn join-item rounded-r-full" onClick={plusQty}>
                +
              </button>
            </div>
            <div>
              <button className="btn btn-secondary text-white rounded-full md:w-[340px] w-full" onClick={async () => {
                if (isAddingToCart || isTogglingWishlist) return;
                setIsAddingToCart(true);
                try { await Promise.resolve(props.addToCart(currentQuantity, relevantPrice)); }
                finally { setIsAddingToCart(false); }
              }} disabled={isAddingToCart || isTogglingWishlist}>
                {isAddingToCart ? <span className="loading loading-spinner loading-md">{" "}</span> : <ShoppingCart className="h-3.5 w-3.5 stroke-white" />}
                {t('field-submit')}
              </button>
            </div>
            <div>
              <button className="btn rounded-full md:w-[340px] w-full" onClick={async () => {
                if (isTogglingWishlist || isAddingToCart) return;
                setIsTogglingWishlist(true);
                try { await Promise.resolve(props.toggleWishlist()); }
                finally { setIsTogglingWishlist(false); }
              }} disabled={isTogglingWishlist || isAddingToCart}>
                {isTogglingWishlist ? <span className="loading loading-spinner loading-md">{" "}</span> : (props.inWishlist ? <HeartSolid className="h-3.5 w-3.5 fill-secondary" /> : <Heart className="h-3.5 w-3.5 stroke-black" />)}
                {t('field-wishlist')}
              </button>
            </div>
            <div className="w-full py-[30px] flex flex-col items-start box-border gap-2.5 text-sm">
              <div className="h-8 flex flex-row items-center gap-2.5">
                <Truck height={24} width={24} />
                <div className="flex-1">
                  {t('advantage_delivery_time')}
                </div>
              </div>
              <div className="h-8 flex flex-row items-center gap-2.5">
                <ShoppingBag height={24} width={24} />
                <div className="flex-1">
                  {t('advantage_delivery_free')}
                </div>
              </div>
              <div className="h-8 flex flex-row items-center gap-2.5">
                <BuildingStorefront height={24} width={24} />
                <div className="flex-1">
                  {t('advantage_showroom')}
                </div>
              </div>
              <div className="h-8 flex flex-row items-center gap-2.5">
                <Globe height={24} width={24} />
                <div className="flex-1">
                  {t('advantage_local_resellers')}
                  {" "}
                  <Link className="p-0 transition-colors text-gray-500 hover:text-gray-300" href={"/standorte"}>{t('advantage_local_resellers_link')}</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
    </>
  )
}

export default ProductSelector;
